import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { defer, of, Subject } from 'rxjs';
import { catchError, finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { AuthService } from 'src/app/services/auth.service';
import { InstallatoerDialogComponent } from '../installatoer-dialog/installatoer-dialog.component';
import { InstallatoerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/installatoer-dto';
import { InstallatoerService } from 'src/app/api/services';
import { GetInstallatoererRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-installatoerer-request';
import { HistoryViewComponent } from '../../../shared/history/history-view/history-view.component';
import { IHistoryView } from '../../../shared/history/history.model';
import { HenviserDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/henviser-dto';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { NotificationService } from 'src/app/notifications/services/notification.service';

@Component({
  selector: 'app-installatoer-list',
  templateUrl: './installatoer-list.component.html',
  styleUrls: ['./installatoer-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InstallatoerListComponent implements OnInit {
  installatoerer: InstallatoerDto[] = [];
  unsubscribe$ = new Subject<void>();
  searchQuery: string;
  form: UntypedFormGroup;
  editForm: UntypedFormGroup;

  displayedColumns: string[] = ['name', 'cvr', 'actions'];

  constructor(
    private installatoerService: InstallatoerService,
    private errorHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private auth: AuthService,
    private router: Router,
    private notifications: NotificationService,
  ) {}

  get canEdit() {
    return this.auth.isAdministrator;
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          this.searchQuery = '';
          const q = queryParams['q'];

          if (q) {
            this.searchQuery = q;
          }

          return this.getInstallatoerer$;
        }),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.form = this.formBuilder.group({
      cvr: ['', [Validators.required, Validators.minLength(1)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
    });

    this.editForm = this.formBuilder.group({
      cvr: ['', [Validators.required, Validators.minLength(1)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  getInstallatoerer$ = defer(() => {
    this.loader.show();
    const request: GetInstallatoererRequest = {
      searchQuery: this.searchQuery,
    };

    return this.installatoerService.getInstallatoerer({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.installatoerer = response;
      }),
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$),
      catchError((err) => {
        this.errorHandler.HandleError(err);
        return of(null);
      }),
    );
  });

  add() {
    let ref = this.dialog.open(InstallatoerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { searchQuery: this.searchQuery },
    });

    ref
      .afterClosed()
      .pipe(
        flatMap(() => {
          this.reset();
          return this.getInstallatoerer$;
        }),
      )
      .subscribe();
  }

  edit(installatoer: InstallatoerDto) {
    let dialogRef = this.dialog.open(InstallatoerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        installatoer: installatoer,
        id: installatoer.id,
        cvr: installatoer.cvr,
        name: installatoer.name,
      },
    });

    dialogRef.afterClosed().subscribe((p) => {
      if (p) {
        const reload = this.historyView && installatoer && installatoer.name != p.name;
        Object.assign(installatoer, p);
        if (reload) this.showhistory(installatoer);
        this.historyViewComponent.render();
      }
    });
  }

  remove(installatoer: InstallatoerDto) {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet installatør?',
        body: 'Er du sikker på at du vil fjerne ' + installatoer.name + '?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        flatMap((confirm) => {
          if (!confirm) return of(null);
          return this.installatoerService
            .removeInstallatoer({
              caller: EGIS_SERVICE_CALLER,
              id: installatoer.id,
            })
            .pipe(
              flatMap(() => {
                return this.getInstallatoerer$;
              }),
            );
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (s) => {
          this.notifications.success(`Installatør med cvr: ${installatoer.cvr} er slettet`);
        },
        (e) => this.errorHandler.HandleError(e),
      );
  }

  reset() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  @ViewChild(HistoryViewComponent) historyViewComponent: HistoryViewComponent;
  historyView: IHistoryView;
  showhistory(row: HenviserDto) {
    this.historyView = { entityId: row.id, historyType: AuditHistoryEnum.Installatoer, title: row.name };
  }
}
